import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Truck, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/api";

export default function PincodeLookup() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter a valid 6-digit Indian pincode.");
      return;
    }
    setLoading(true);
    try {
      const data = await api.lookupPincode(trimmed);
      setResult(data);
    } catch (err) {
      setError(err.message || "Lookup failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="section-lg pincode-band" data-testid="pincode-band">
      <div className="wrap">
        <div className="pincode-grid">
          <div>
            <div className="section-eyebrow">Serving your pincode?</div>
            <h2 style={{ maxWidth: "18ch" }}>Which of our warehouses is nearest to you.</h2>
            <p className="lead" style={{ maxWidth: "48ch", color: "var(--tc-slate)", fontSize: 16, lineHeight: 1.6, marginTop: 8 }}>
              Enter your delivery pincode — we&apos;ll tell you the nearest of our six
              warehouses (Coimbatore, Chennai, Cochin, Bangalore, Dahej, Mumbai) and
              indicative road-tanker lead time.
            </p>
          </div>

          <form className="pincode-card" onSubmit={submit} noValidate data-testid="pincode-form">
            <label htmlFor="pincode-input" className="pincode-label">Delivery pincode</label>
            <div className="pincode-row">
              <input
                id="pincode-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="e.g. 641002"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="pincode-input"
              />
              <button
                type="submit"
                className="btn btn-brand"
                disabled={loading}
                data-testid="pincode-submit"
              >
                {loading ? <Loader2 size={16} className="spin" /> : <>Check <ArrowRight size={16} strokeWidth={2.5} /></>}
              </button>
            </div>

            {error && (
              <div className="pincode-error" data-testid="pincode-error">{error}</div>
            )}

            {result && (
              <motion.div
                className="pincode-result"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                data-testid="pincode-result"
              >
                <div className="pincode-result-head">
                  <MapPin size={18} strokeWidth={2.2} />
                  <span>PIN <b>{result.pincode}</b> · {result.region}</span>
                </div>
                <div className="pincode-result-row">
                  <div>
                    <div className="pincode-result-label">Nearest warehouse</div>
                    <div className="pincode-result-value" data-testid="pincode-warehouse">{result.warehouse}</div>
                  </div>
                  <div>
                    <div className="pincode-result-label">Lead time</div>
                    <div className="pincode-result-value with-icon" data-testid="pincode-leadtime">
                      <Truck size={16} strokeWidth={2.2} />
                      {result.lead_time}
                    </div>
                  </div>
                </div>
                <p className="pincode-note">{result.note}</p>
              </motion.div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
